import { useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { ChevronLeft, ChevronRight, Heart, Zap, Shield, Users, Sparkles, MapPin } from "lucide-react";

export default function About() {
  const navigate = useNavigate();
  const { lang, isRTL } = useLang();
  const Chevron = isRTL ? ChevronRight : ChevronLeft;

  const values = [
    {
      icon: Zap,
      title: lang === "ar" ? "سرعة في التوصيل" : "Fast delivery",
      desc: lang === "ar" ? "متوسط توصيل من 25-45 دقيقة لكل طلب" : "Average delivery 25-45 minutes per order",
    },
    {
      icon: Heart,
      title: lang === "ar" ? "خدمة عملاء بحرة" : "Local Bahrah service",
      desc: lang === "ar" ? "نخدم منطقتنا أوّلاً، نعرف احتياجات حيّكم" : "We serve our area first, we know your needs",
    },
    {
      icon: Shield,
      title: lang === "ar" ? "ضمان الجودة" : "Quality guarantee",
      desc: lang === "ar" ? "نختار شركاءنا بعناية لنضمن لك أفضل تجربة" : "Carefully selected partners for the best experience",
    },
    {
      icon: Sparkles,
      title: lang === "ar" ? "تجربة بسيطة" : "Simple experience",
      desc: lang === "ar" ? "اطلب بضغطات قليلة وتابع طلبك مباشرة" : "Order in a few taps and track in real time",
    },
  ];

  const stats = [
    { value: "2,000+", label: lang === "ar" ? "عميل" : "Customers" },
    { value: "10K+",   label: lang === "ar" ? "طلب" : "Orders" },
    { value: "4.8",    label: lang === "ar" ? "تقييم" : "Rating" },
  ];

  return (
    <div className="max-w-md mx-auto bg-white min-h-screen pb-20">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-white border-b border-[#F2F2F2] px-5 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1" data-testid="about-back" aria-label="back">
          <Chevron className="w-6 h-6 text-[#1A1A1A]" strokeWidth={2.5} />
        </button>
        <h1 className="font-bold text-lg text-[#1A1A1A]">{lang === "ar" ? "عن جايك" : "About Jayek"}</h1>
      </div>

      <div className="px-5 pt-6">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl mb-4 shadow-lg" style={{ background: "linear-gradient(135deg, #E05D3A 0%, #C24B2C 100%)" }}>
            <span className="font-display text-4xl font-black text-white">{lang === "ar" ? "جايك" : "J"}</span>
          </div>
          <h2 className="font-display text-3xl font-black text-[#1A1A1A] mb-2">
            {lang === "ar" ? "جايك للتوصيل" : "Jayek Delivery"}
          </h2>
          <p className="text-[#6B6B6B] text-sm">
            {lang === "ar" ? "النسخة 1.0.0" : "Version 1.0.0"}
          </p>
        </div>

        {/* Mission */}
        <div className="rounded-3xl p-6 mb-6 border border-[#FEF0EE] bg-[#FFF8F5]">
          <h3 className="font-bold text-[#E05D3A] text-sm mb-2 uppercase tracking-wider">
            {lang === "ar" ? "رسالتنا" : "Our mission"}
          </h3>
          <p className="text-[#1A1A1A] leading-relaxed text-[15px]">
            {lang === "ar"
              ? "جايك تطبيق سعودي يربط سكان بحرة بأفضل المتاجر المحلية من سوبرماركت ومنتجات تجميل وأكثر. هدفنا توفير تجربة توصيل سريعة وموثوقة بأسعار عادلة، مع دعم المتاجر المحلية وتمكينها من الوصول لعملاء أوسع."
              : "Jayek is a Saudi delivery app connecting Bahrah residents with the best local stores—supermarkets, beauty products, and more. Our mission is fast, reliable delivery at fair prices, while empowering local businesses to reach more customers."}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          {stats.map((s) => (
            <div key={s.label} className="bg-[#FAFAFA] rounded-2xl p-4 text-center">
              <p className="font-display text-2xl font-black text-[#E05D3A]">{s.value}</p>
              <p className="text-xs text-[#6B6B6B] mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Values */}
        <h3 className="font-bold text-[#1A1A1A] text-lg mb-4">
          {lang === "ar" ? "قيمنا" : "Our values"}
        </h3>
        <div className="space-y-3 mb-8">
          {values.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex items-start gap-3 bg-white border border-[#F2F2F2] rounded-2xl p-4">
              <div className="w-11 h-11 rounded-2xl bg-[#FEF0EE] flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-[#E05D3A]" strokeWidth={2.2} />
              </div>
              <div className="flex-1">
                <p className="font-bold text-[15px] text-[#1A1A1A] mb-0.5">{title}</p>
                <p className="text-xs text-[#6B6B6B] leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Coverage */}
        <div className="rounded-2xl border border-[#F2F2F2] p-5 bg-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#FEF0EE] flex items-center justify-center">
              <MapPin className="w-5 h-5 text-[#E05D3A]" strokeWidth={2.2} />
            </div>
            <h3 className="font-bold text-[#1A1A1A]">
              {lang === "ar" ? "مناطق التغطية" : "Coverage areas"}
            </h3>
          </div>
          <p className="text-[#6B6B6B] text-sm leading-relaxed">
            {lang === "ar"
              ? "نخدم حالياً جميع أحياء بحرة في غرب المملكة. نخطط للتوسع قريباً إلى ذهبان، الجموم، وأبرز مدن مكة المكرمة."
              : "Currently serving all Bahrah districts in western Saudi Arabia. Expanding soon to Dhahban, Al-Jamum, and major cities of Makkah region."}
          </p>
        </div>

        {/* Team */}
        <div className="mt-6 flex items-center gap-3 text-xs text-[#8B8B8B] justify-center">
          <Users className="w-4 h-4" />
          <span>{lang === "ar" ? "صُنع بحب في المملكة العربية السعودية" : "Made with love in Saudi Arabia"}</span>
        </div>
      </div>
    </div>
  );
}
